import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fromInput, toInput, TASK_TYPES, MEETING_TYPES } from "@/lib/api";
import { Sel, Field } from "@/components/common";
import dayjs from "dayjs";

const LeadPicker = ({ value, onChange }) => {
  const [leads, setLeads] = useState([]);
  useEffect(() => { api.get("/leads").then((r) => setLeads(r.data)); }, []);
  return <Sel testid="lead-picker" value={value} onChange={onChange} placeholder="Select lead / client…" options={leads.map((l) => ({ value: l.id, label: `${l.company} — ${l.contact_person || ""}` }))} />;
};

const Shell = ({ open, onOpenChange, title, children, onSubmit, busy, testid, submitLabel = "Save" }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid={testid}>
      <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
      <form onSubmit={onSubmit} className="space-y-3">
        {children}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={() => onOpenChange(false)} className="h-9 rounded-md border px-4 text-sm font-semibold hover:bg-slate-50">Cancel</button>
          <button data-testid={`${testid}-submit`} disabled={busy} className="h-9 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">{busy ? "Saving…" : submitLabel}</button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

const useSubmit = (fn, onDone, onOpenChange, msg) => {
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await fn(); toast.success(msg); onOpenChange(false); onDone && onDone(); }
    catch (ex) { toast.error(errMsg(ex)); }
    finally { setBusy(false); }
  };
  return [busy, submit];
};

export const CallDialog = ({ open, onOpenChange, lead, onDone }) => {
  const { meta } = useAuth();
  const init = () => ({ lead_id: lead?.id || "", status: "Connected", called_at: toInput(new Date()), duration_min: "", notes: "", next_follow_up: "", stage: "" });
  const [f, setF] = useState(init);
  useEffect(() => { if (open) setF(init()); }, [open]); // eslint-disable-line
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const [busy, submit] = useSubmit(() => {
    if (!f.lead_id) throw new Error("Select a lead");
    return api.post("/calls", { ...f, called_at: fromInput(f.called_at), next_follow_up: fromInput(f.next_follow_up), duration_min: Number(f.duration_min || 0), stage: f.stage || null });
  }, onDone, onOpenChange, "Call logged");
  return (
    <Shell open={open} onOpenChange={onOpenChange} title={`Log call${lead ? ` — ${lead.company}` : ""}`} onSubmit={submit} busy={busy} testid="call-dialog">
      {!lead && <Field label="Lead"><LeadPicker value={f.lead_id} onChange={set("lead_id")} /></Field>}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Call status"><Sel testid="call-status-select" value={f.status} onChange={set("status")} options={meta.call_statuses} /></Field>
        <Field label="Duration (min)"><input data-testid="call-duration-input" type="number" min="0" step="0.5" className="crm-input" value={f.duration_min} onChange={(e) => set("duration_min")(e.target.value)} /></Field>
      </div>
      <Field label="Date / time"><input data-testid="call-datetime-input" type="datetime-local" className="crm-input" value={f.called_at} onChange={(e) => set("called_at")(e.target.value)} /></Field>
      <Field label="Call notes"><textarea data-testid="call-notes-input" rows={3} className="crm-input h-auto py-2" value={f.notes} onChange={(e) => set("notes")(e.target.value)} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Next follow-up"><input data-testid="call-followup-input" type="datetime-local" className="crm-input" value={f.next_follow_up} onChange={(e) => set("next_follow_up")(e.target.value)} /></Field>
        <Field label="Update lead stage"><Sel testid="call-stage-select" value={f.stage} onChange={set("stage")} placeholder="Keep current" options={meta.stages} /></Field>
      </div>
    </Shell>
  );
};

export const TaskDialog = ({ open, onOpenChange, lead, onDone }) => {
  const { isAdmin, salesUsers } = useAuth();
  const init = () => ({ lead_id: lead?.id || "", title: lead ? `Follow up with ${lead.company}` : "", type: "Follow-up", due_at: toInput(dayjs().add(1, "day").hour(11).minute(0)), notes: "", user_id: "" });
  const [f, setF] = useState(init);
  useEffect(() => { if (open) setF(init()); }, [open]); // eslint-disable-line
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const [busy, submit] = useSubmit(() => api.post("/tasks", { ...f, lead_id: f.lead_id || null, user_id: f.user_id || null, due_at: fromInput(f.due_at) }), onDone, onOpenChange, "Follow-up created");
  return (
    <Shell open={open} onOpenChange={onOpenChange} title="New follow-up / task" onSubmit={submit} busy={busy} testid="task-dialog">
      {!lead && <Field label="Lead (optional)"><LeadPicker value={f.lead_id} onChange={set("lead_id")} /></Field>}
      <Field label="Next action / title"><input data-testid="task-title-input" required className="crm-input" value={f.title} onChange={(e) => set("title")(e.target.value)} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Task type"><Sel testid="task-type-select" value={f.type} onChange={set("type")} options={TASK_TYPES} /></Field>
        <Field label="Due date & time"><input data-testid="task-due-input" type="datetime-local" required className="crm-input" value={f.due_at} onChange={(e) => set("due_at")(e.target.value)} /></Field>
      </div>
      {isAdmin && !f.lead_id && <Field label="Assign to"><Sel value={f.user_id} onChange={set("user_id")} placeholder="Myself" options={salesUsers.map((u) => ({ value: u.id, label: u.name }))} /></Field>}
      <Field label="Notes"><textarea data-testid="task-notes-input" rows={2} className="crm-input h-auto py-2" value={f.notes} onChange={(e) => set("notes")(e.target.value)} /></Field>
    </Shell>
  );
};

export const MeetingDialog = ({ open, onOpenChange, lead, onDone }) => {
  const init = () => ({ lead_id: lead?.id || "", title: "", type: "Video Call", scheduled_at: toInput(dayjs().add(1, "day").hour(15).minute(0)), duration_min: 30, location: "", notes: "" });
  const [f, setF] = useState(init);
  useEffect(() => { if (open) setF(init()); }, [open]); // eslint-disable-line
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const [busy, submit] = useSubmit(() => {
    if (!f.lead_id) throw new Error("Select a lead");
    return api.post("/meetings", { ...f, scheduled_at: fromInput(f.scheduled_at), duration_min: Number(f.duration_min || 30) });
  }, onDone, onOpenChange, "Meeting scheduled");
  return (
    <Shell open={open} onOpenChange={onOpenChange} title={`Schedule meeting${lead ? ` — ${lead.company}` : ""}`} onSubmit={submit} busy={busy} testid="meeting-dialog">
      {!lead && <Field label="Client / lead"><LeadPicker value={f.lead_id} onChange={set("lead_id")} /></Field>}
      <Field label="Title"><input data-testid="meeting-title-input" className="crm-input" placeholder="Discovery meeting" value={f.title} onChange={(e) => set("title")(e.target.value)} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Meeting type"><Sel testid="meeting-type-select" value={f.type} onChange={set("type")} options={MEETING_TYPES} /></Field>
        <Field label="Duration (min)"><input type="number" className="crm-input" value={f.duration_min} onChange={(e) => set("duration_min")(e.target.value)} /></Field>
      </div>
      <Field label="Date & time"><input data-testid="meeting-datetime-input" type="datetime-local" required className="crm-input" value={f.scheduled_at} onChange={(e) => set("scheduled_at")(e.target.value)} /></Field>
      <Field label="Location / link"><input className="crm-input" value={f.location} onChange={(e) => set("location")(e.target.value)} /></Field>
      <Field label="Notes"><textarea rows={2} className="crm-input h-auto py-2" value={f.notes} onChange={(e) => set("notes")(e.target.value)} /></Field>
    </Shell>
  );
};

export const MeetingUpdateDialog = ({ open, onOpenChange, meeting, onDone }) => {
  const { meta } = useAuth();
  const init = () => ({ status: meeting?.status || "Scheduled", scheduled_at: toInput(meeting?.scheduled_at), outcome: meeting?.outcome || "", notes: meeting?.notes || "", next_action: meeting?.next_action || "", follow_up_date: toInput(meeting?.follow_up_date) });
  const [f, setF] = useState(init);
  useEffect(() => { if (open) setF(init()); }, [open, meeting]); // eslint-disable-line
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const [busy, submit] = useSubmit(() => {
    const body = { ...f, scheduled_at: fromInput(f.scheduled_at), follow_up_date: fromInput(f.follow_up_date) };
    if (toInput(meeting.scheduled_at) === f.scheduled_at) delete body.scheduled_at;
    return api.patch(`/meetings/${meeting.id}`, body);
  }, onDone, onOpenChange, "Meeting updated");
  if (!meeting) return null;
  return (
    <Shell open={open} onOpenChange={onOpenChange} title={`Update meeting — ${meeting.client}`} onSubmit={submit} busy={busy} testid="meeting-update-dialog">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Status"><Sel testid="meeting-status-select" value={f.status} onChange={set("status")} options={meta.meeting_statuses} /></Field>
        <Field label="Date & time"><input data-testid="meeting-reschedule-input" type="datetime-local" className="crm-input" value={f.scheduled_at} onChange={(e) => set("scheduled_at")(e.target.value)} /></Field>
      </div>
      <Field label="Outcome"><textarea data-testid="meeting-outcome-input" rows={2} className="crm-input h-auto py-2" value={f.outcome} onChange={(e) => set("outcome")(e.target.value)} /></Field>
      <Field label="Notes"><textarea rows={2} className="crm-input h-auto py-2" value={f.notes} onChange={(e) => set("notes")(e.target.value)} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Next action"><input data-testid="meeting-next-action-input" className="crm-input" value={f.next_action} onChange={(e) => set("next_action")(e.target.value)} /></Field>
        <Field label="Follow-up date"><input data-testid="meeting-followup-input" type="datetime-local" className="crm-input" value={f.follow_up_date} onChange={(e) => set("follow_up_date")(e.target.value)} /></Field>
      </div>
    </Shell>
  );
};
